// Bounding box + 8 resize handles for a multi-element selection.
// Resizing scales every selected element about the union's opposite corner.
// Negative scale (dragging a handle past the opposite side) flips the group,
// matching single-element reverse-drawing behaviour.
import React, { useRef } from "react";
import { unionBbox, resizeElement, translateElement, bboxOf } from "./canvasUtils";

const HANDLES = [
  { k: "nw", x: 0, y: 0 },
  { k: "n", x: 0.5, y: 0 },
  { k: "ne", x: 1, y: 0 },
  { k: "e", x: 1, y: 0.5 },
  { k: "se", x: 1, y: 1 },
  { k: "s", x: 0.5, y: 1 },
  { k: "sw", x: 0, y: 1 },
  { k: "w", x: 0, y: 0.5 },
];

export default function MultiSelectionOverlay({
  elements,
  scale = 1,
  getSvgPoint,
  onLiveUpdateMany,
  onResizeStart,
  onResizeEnd,
  onSelect,
}) {
  const dragRef = useRef(null);
  const bb = unionBbox(elements);
  if (bb.w === 0 && bb.h === 0) return null;

  const handleSize = 9 / scale;
  const padding = 6 / scale;
  const x = bb.x - padding;
  const y = bb.y - padding;
  const w = bb.w + padding * 2;
  const h = bb.h + padding * 2;

  // Drag anywhere inside the selection box to move the whole group.
  const onBoxPointerDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const startPt = getSvgPoint(e.clientX, e.clientY);
    const originals = elements.map((el) => ({ id: el.id, el }));
    e.target.setPointerCapture?.(e.pointerId);
    let moved = false;
    let checkpointed = false;

    const onMove = (ev) => {
      const p = getSvgPoint(ev.clientX, ev.clientY);
      const dx = p.x - startPt.x;
      const dy = p.y - startPt.y;
      if (!moved && Math.abs(dx) + Math.abs(dy) < 2 / scale) return;
      if (!checkpointed) {
        onResizeStart?.();
        checkpointed = true;
      }
      moved = true;
      onLiveUpdateMany(originals.map((o) => ({ id: o.id, attrs: translateElement(o.el, dx, dy) })));
    };
    const onUp = (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      onResizeEnd?.();
      // A plain click (no drag) inside the box selects the single element hit,
      // so users can still drill into one object within the selection.
      if (!moved && onSelect) {
        const p = getSvgPoint(ev.clientX, ev.clientY);
        const additive = ev.shiftKey || ev.metaKey || ev.ctrlKey;
        const hit = [...elements].reverse().find((el) => {
          const b = bboxOf(el);
          return p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h;
        });
        onSelect(hit ? hit.id : null, additive);
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const onHandlePointerDown = (e, k) => {
    e.stopPropagation();
    e.preventDefault();
    const startPt = getSvgPoint(e.clientX, e.clientY);
    onResizeStart?.();
    e.target.setPointerCapture?.(e.pointerId);
    const b = bb;
    const originals = elements.map((el) => ({ id: el.id, el }));
    let ox = b.x;
    let oy = b.y;
    if (k.includes("w")) ox = b.x + b.w;
    if (k.includes("n")) oy = b.y + b.h;
    dragRef.current = true;

    const onMove = (ev) => {
      if (!dragRef.current) return;
      const p = getSvgPoint(ev.clientX, ev.clientY);
      const dx = p.x - startPt.x;
      const dy = p.y - startPt.y;
      let sx = 1;
      let sy = 1;
      if (k.includes("e")) sx = b.w === 0 ? 1 : (b.w + dx) / b.w;
      if (k.includes("w")) sx = b.w === 0 ? 1 : (b.w - dx) / b.w;
      if (k.includes("s")) sy = b.h === 0 ? 1 : (b.h + dy) / b.h;
      if (k.includes("n")) sy = b.h === 0 ? 1 : (b.h - dy) / b.h;
      onLiveUpdateMany(
        originals.map((o) => {
          const attrs = resizeElement(o.el, o.el, sx, sy, ox, oy);
          const type = attrs.__type;
          if (type) delete attrs.__type;
          return { id: o.id, attrs, type };
        })
      );
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      onResizeEnd?.();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const cursorOf = (k) => {
    const map = { n: "ns", s: "ns", e: "ew", w: "ew", nw: "nwse", se: "nwse", ne: "nesw", sw: "nesw" };
    return `${map[k]}-resize`;
  };

  return (
    <g pointerEvents="auto" data-testid="multi-selection-overlay">
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill="transparent"
        style={{ cursor: "move" }}
        onPointerDown={onBoxPointerDown}
        data-testid="multi-move-surface"
      />
      <rect x={x} y={y} width={w} height={h} className="multi-selection-box" pointerEvents="none" />
      {HANDLES.map((h2) => {
        const hx = x + w * h2.x - handleSize / 2;
        const hy = y + h * h2.y - handleSize / 2;
        return (
          <rect
            key={h2.k}
            x={hx}
            y={hy}
            width={handleSize}
            height={handleSize}
            className="selection-handle"
            style={{ cursor: cursorOf(h2.k) }}
            onPointerDown={(e) => onHandlePointerDown(e, h2.k)}
            data-testid={`multi-resize-handle-${h2.k}`}
          />
        );
      })}
    </g>
  );
}
